import { useState } from 'react';
import MainLayout from '@/components/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Palmtree, MapPin, Sprout } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DATOS DE EJEMPLO - Lotes de la finca
const LOTES_EJEMPLO = [
  { id: '1', nombre: 'Agropal', total_centros: 25, color: 'bg-emerald-100 border-emerald-300' },
  { id: '2', nombre: 'La Isla', total_centros: 29, color: 'bg-blue-100 border-blue-300' },
  { id: '3', nombre: 'Cenízaro', total_centros: 11, color: 'bg-amber-100 border-amber-300' },
  { id: '4', nombre: 'Galerón', total_centros: 7, color: 'bg-purple-100 border-purple-300' },
  { id: '5', nombre: 'Laurel', total_centros: 17, color: 'bg-green-100 border-green-300' },
  { id: '6', nombre: 'La Montaña', total_centros: 19, color: 'bg-teal-100 border-teal-300' },
];

// DATOS DE EJEMPLO - Parcelas de Agropal (según la imagen)
const PARCELAS_AGROPAL = [
  { id: 'p1', lote_id: '1', numero: 1, nombre: 'AGROPAL - Río', centros: 13 },
  { id: 'p2', lote_id: '1', numero: 2, nombre: 'AGROPAL - Arrocera', centros: 5 },
  { id: 'p3', lote_id: '1', numero: 3, nombre: 'AGROPAL - Laguna', centros: 7 },
];

// DATOS DE EJEMPLO - Centros de AGROPAL - Río (según la imagen)
const CENTROS_EJEMPLO = [
  { numero: 1, cant_zurcos: 5, zurcos_izq: 3, matas_izq: 31, zurcos_der: 2, matas_der: 28, total: 59 },
  { numero: 2, cant_zurcos: 4, zurcos_izq: 2, matas_izq: 31, zurcos_der: 2, matas_der: 35, total: 66 },
  { numero: 3, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 38, zurcos_der: 2, matas_der: 41, total: 79 },
  { numero: 4, cant_zurcos: 4, zurcos_izq: 2, matas_izq: 44, zurcos_der: 2, matas_der: 45, total: 89 },
  { numero: 5, cant_zurcos: 4, zurcos_izq: 2, matas_izq: 51, zurcos_der: 2, matas_der: 60, total: 111 },
  { numero: 6, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 66, zurcos_der: 1, matas_der: 35, total: 101 },
  { numero: 7, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 65, zurcos_der: 1, matas_der: 34, total: 99 },
  { numero: 8, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 62, zurcos_der: 1, matas_der: 31, total: 93 },
  { numero: 9, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 57, zurcos_der: 1, matas_der: 27, total: 84 },
  { numero: 10, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 44, zurcos_der: 1, matas_der: 23, total: 67 },
  { numero: 11, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 39, zurcos_der: 1, matas_der: 20, total: 59 },
  { numero: 12, cant_zurcos: 3, zurcos_izq: 2, matas_izq: 32, zurcos_der: 1, matas_der: 14, total: 46 },
  { numero: 13, cant_zurcos: 6, zurcos_izq: 2, matas_izq: 27, zurcos_der: 4, matas_der: 46, total: 73 },
];

export default function GestionFinca({ user, onLogout }) {
  const [vistaActual, setVistaActual] = useState('lotes'); // 'lotes', 'parcelas', 'centros'
  const [loteSeleccionado, setLoteSeleccionado] = useState(null);
  const [parcelaSeleccionada, setParcelaSeleccionada] = useState(null);

  const handleVolverLotes = () => {
    setVistaActual('lotes');
    setLoteSeleccionado(null);
    setParcelaSeleccionada(null);
  };

  const handleVolverParcelas = () => {
    setVistaActual('parcelas');
    setParcelaSeleccionada(null);
  };

  const handleSeleccionarLote = (lote) => {
    setLoteSeleccionado(lote);
    setVistaActual('parcelas');
  };

  const handleSeleccionarParcela = (parcela) => {
    setParcelaSeleccionada(parcela);
    setVistaActual('centros');
  };

  // Calcular totales de la tabla de centros
  const calcularTotales = () => {
    return CENTROS_EJEMPLO.reduce((acc, centro) => ({
      total_matas: acc.total_matas + centro.total,
      total_matas_izq: acc.total_matas_izq + centro.matas_izq,
      total_matas_der: acc.total_matas_der + centro.matas_der,
    }), { total_matas: 0, total_matas_izq: 0, total_matas_der: 0 });
  };

  const totales = calcularTotales();

  // VISTA: LOTES
  const renderVistaLotes = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Palmtree className="w-8 h-8 text-emerald-600" />
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Finca</h1>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          6 Lotes | 108 Centros Totales
        </Badge>
      </div>

      {/* Grid de Lotes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {LOTES_EJEMPLO.map((lote) => (
          <Card
            key={lote.id}
            className={`cursor-pointer border-2 ${lote.color} hover:shadow-lg transition-all transform hover:scale-105`}
            onClick={() => handleSeleccionarLote(lote)}
          >
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-xl">
                <span className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-emerald-700" />
                  {lote.nombre}
                </span>
                <Badge className="bg-emerald-600">{lote.total_centros}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  <strong>Centros fruteros:</strong> {lote.total_centros}
                </p>
                <Button variant="outline" className="w-full mt-2" size="sm">
                  Ver Parcelas →
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // VISTA: PARCELAS
  const renderVistaParcelas = () => (
    <div className="space-y-6">
      {/* Header con botón volver */}
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={handleVolverLotes}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Lotes
        </Button>
        <div className="flex items-center space-x-3">
          <MapPin className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{loteSeleccionado?.nombre}</h1>
            <p className="text-sm text-gray-500">{loteSeleccionado?.total_centros} centros fruteros</p>
          </div>
        </div>
      </div>

      {/* Grid de Parcelas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PARCELAS_AGROPAL.map((parcela) => (
          <Card
            key={parcela.id}
            className="cursor-pointer border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all"
            onClick={() => handleSeleccionarParcela(parcela)}
          >
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="text-lg">Parcela {parcela.numero}</span>
                <Badge variant="outline">{parcela.centros} centros</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">{parcela.nombre}</p>
                <p className="text-xs text-gray-500">{parcela.centros} centros fruteros</p>
                <Button variant="outline" className="w-full mt-2" size="sm">
                  Ver Centros →
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // VISTA: CENTROS (Tabla detallada)
  const renderVistaCentros = () => (
    <div className="space-y-6">
      {/* Header con navegación */}
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={handleVolverParcelas}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Parcelas
        </Button>
        <div className="flex items-center space-x-3">
          <Sprout className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {parcelaSeleccionada?.nombre}
            </h1>
            <p className="text-sm text-gray-500">
              {loteSeleccionado?.nombre} / Parcela {parcelaSeleccionada?.numero}
            </p>
          </div>
        </div>
      </div>

      {/* Tabla de Centros */}
      <Card>
        <CardHeader>
          <CardTitle>Centros Fruteros - Detalle de Matas y Zurcos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-emerald-600 text-white">
                  <th className="border border-emerald-700 px-3 py-2 text-sm"># CENTRO</th>
                  <th className="border border-emerald-700 px-3 py-2 text-sm">CANT. ZURCOS</th>
                  <th className="border border-emerald-700 px-3 py-2 text-sm">ZURCOS IZQ.</th>
                  <th className="border border-emerald-700 px-3 py-2 text-sm">MATAS IZQ.</th>
                  <th className="border border-emerald-700 px-3 py-2 text-sm">ZURCOS DER.</th>
                  <th className="border border-emerald-700 px-3 py-2 text-sm">MATAS DER.</th>
                  <th className="border border-emerald-700 px-3 py-2 text-sm font-bold">TOTAL MATAS</th>
                </tr>
              </thead>
              <tbody>
                {CENTROS_EJEMPLO.map((centro, idx) => (
                  <tr
                    key={centro.numero}
                    className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="border border-gray-300 px-3 py-2 text-center font-semibold">
                      {centro.numero}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {centro.cant_zurcos}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {centro.zurcos_izq}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center text-blue-700 font-medium">
                      {centro.matas_izq}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {centro.zurcos_der}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center text-purple-700 font-medium">
                      {centro.matas_der}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center bg-emerald-50 font-bold text-emerald-900">
                      {centro.total}
                    </td>
                  </tr>
                ))}
                {/* Fila de totales */}
                <tr className="bg-emerald-100 font-bold">
                  <td colSpan="3" className="border border-emerald-700 px-3 py-2 text-right">
                    TOTAL:
                  </td>
                  <td className="border border-emerald-700 px-3 py-2 text-center text-blue-900">
                    {totales.total_matas_izq}
                  </td>
                  <td className="border border-emerald-700 px-3 py-2"></td>
                  <td className="border border-emerald-700 px-3 py-2 text-center text-purple-900">
                    {totales.total_matas_der}
                  </td>
                  <td className="border border-emerald-700 px-3 py-2 text-center bg-emerald-200 text-emerald-900 text-lg">
                    {totales.total_matas}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Resumen estadístico */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Matas Izquierda</p>
                <p className="text-2xl font-bold text-blue-700">{totales.total_matas_izq}</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Matas Derecha</p>
                <p className="text-2xl font-bold text-purple-700">{totales.total_matas_der}</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-gray-600">Total Matas</p>
                <p className="text-2xl font-bold text-emerald-700">{totales.total_matas}</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {vistaActual === 'lotes' && renderVistaLotes()}
        {vistaActual === 'parcelas' && renderVistaParcelas()}
        {vistaActual === 'centros' && renderVistaCentros()}
      </div>
    </MainLayout>
  );
}
